import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";
import { deviceUsageInfoDtoSchema } from "../../../shared/capacity/overCapacity";

const required = z.string().trim().min(1);

export const deviceStatusDtoSchema = z.enum(["ACTIVE", "INACTIVE"]);

export const deviceTypeDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  createdAt: required,
  updatedAt: required,
});

export const deviceTypesListResponseDtoSchema = z.object({
  data: z.array(deviceTypeDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const deviceBrandDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  createdAt: required,
  updatedAt: required,
});

export const deviceBrandsListResponseDtoSchema = z.object({
  data: z.array(deviceBrandDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const deviceModelDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  deviceBrandId: required,
  deviceTypeId: required,
  createdAt: required,
  updatedAt: required,
});

export const deviceModelsListResponseDtoSchema = z.object({
  data: z.array(deviceModelDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const deviceCreateDtoSchema = z.object({
  accountId: required.optional(),
  deviceModelId: required.optional(),
  imei: required,
  simNumber: required,
  serialNumber: required,
  assetId: required,
  expiryDate: required,
  notes: z.string().trim().optional(),
});

// The shared {id, nameEn, nameAr} shape reused for account/deviceType/
// deviceBrand/deviceModel on the create response.
export const deviceRefDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
});

export const deviceAssetRefDtoSchema = z.object({
  id: required,
  code: required,
  nameEn: required,
  nameAr: required,
});

export const deviceDtoSchema = z.object({
  id: required,
  code: required,
  imei: required,
  simNumber: required,
  serialNumber: required,
  notes: z.string().nullable(),
  status: deviceStatusDtoSchema,
  installationDate: required,
  expiryDate: required,
  createdAt: required,
  updatedAt: required,
  account: deviceRefDtoSchema,
  // Null until an asset is linked -- devices can be created (and left,
  // per DeviceUpdateInput's optional assetId) without one.
  asset: deviceAssetRefDtoSchema.nullable(),
  deviceType: deviceRefDtoSchema,
  deviceBrand: deviceRefDtoSchema,
  deviceModel: deviceRefDtoSchema,
});

export const deviceResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: deviceDtoSchema,
});

// Create, restore and activate also say where the world's devices now stand.
export const deviceWriteResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: deviceDtoSchema.extend({ usageInfo: deviceUsageInfoDtoSchema }),
});

// `GET /devices/capacity`: the devices NASAM sold this world for all its
// clients together. `remaining` goes negative once over.
export const deviceCapacityResponseDtoSchema = z.object({
  data: z.object({
    quantity: z.number(),
    used: z.number(),
    remaining: z.number(),
  }),
});

// `POST /devices` answers 201 for two outcomes. A created device carries a
// full `deviceDtoSchema`; when the submitted identifiers match an
// already-archived device, `data` is this trimmed summary instead --
// distinguishable from a real device by missing required fields like
// `status`/`account`/`asset`, not by `isArchived` (present but not itself
// validated here).
export const archivedDeviceMatchDtoSchema = z.object({
  id: required,
  code: required,
  imei: required,
  simNumber: required,
  serialNumber: required,
});

export const archivedDeviceMatchResponseDtoSchema = z.object({
  data: archivedDeviceMatchDtoSchema,
  message: required,
});

// `PATCH /devices/:id` -- only these two move. `notes: null` clears it;
// leaving a key out keeps its current value.
export const deviceUpdateDtoSchema = z.object({
  assetId: required.optional(),
  notes: z.string().trim().nullable().optional(),
});

// `GET /devices` -- lighter than deviceDtoSchema: no notes, simNumber or
// serialNumber, just enough to list and act on. Reuses the same embedded
// account/asset/deviceType/deviceBrand/deviceModel shapes.
export const deviceListItemDtoSchema = z.object({
  id: required,
  code: required,
  imei: required,
  status: deviceStatusDtoSchema,
  expiryDate: required,
  createdAt: required,
  updatedAt: required,
  account: deviceRefDtoSchema,
  // Null until an asset is linked -- see deviceDtoSchema's own asset field.
  asset: deviceAssetRefDtoSchema.nullable(),
  deviceType: deviceRefDtoSchema,
  deviceBrand: deviceRefDtoSchema,
  deviceModel: deviceRefDtoSchema,
});

export const devicesListResponseDtoSchema = z.object({
  data: z.array(deviceListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

// `GET /devices/:id` -- otherwise identical to deviceDtoSchema, plus
// sensorMappings (always empty for now -- sensor assignment isn't
// available yet). Not interpreted further than "is it empty".
export const deviceDetailDtoSchema = z.object({
  id: required,
  code: required,
  imei: required,
  simNumber: required,
  serialNumber: required,
  notes: z.string().nullable(),
  status: deviceStatusDtoSchema,
  installationDate: required,
  expiryDate: required,
  createdAt: required,
  updatedAt: required,
  account: deviceRefDtoSchema,
  // Null until an asset is linked -- see deviceDtoSchema's own asset field.
  asset: deviceAssetRefDtoSchema.nullable(),
  deviceType: deviceRefDtoSchema,
  deviceBrand: deviceRefDtoSchema,
  deviceModel: deviceRefDtoSchema,
  sensorMappings: z.array(z.unknown()),
});

export const deviceDetailResponseDtoSchema = z.object({
  data: deviceDetailDtoSchema,
});

// `DELETE /devices/:id` -- a bare `{ message }` envelope, no record.
export const deviceDeleteResponseDtoSchema = z.object({
  message: required,
});

// `GET /devices/archived` -- the same row shape as GET /devices plus
// archivedAt; trimmed to just what the Archived Devices screen renders (the
// full response also carries status/deviceType/expiryDate/createdAt/
// updatedAt, which zod drops since this schema doesn't declare them).
export const archivedDeviceListItemDtoSchema = z.object({
  id: required,
  code: required,
  imei: required,
  account: deviceRefDtoSchema,
  // Null until an asset is linked -- see deviceDtoSchema's own asset field.
  asset: deviceAssetRefDtoSchema.nullable(),
  deviceBrand: deviceRefDtoSchema,
  deviceModel: deviceRefDtoSchema,
  archivedAt: required,
});

export const archivedDevicesListResponseDtoSchema = z.object({
  data: z.array(archivedDeviceListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export type DeviceTypeDto = z.infer<typeof deviceTypeDtoSchema>;
export type DeviceBrandDto = z.infer<typeof deviceBrandDtoSchema>;
export type DeviceModelDto = z.infer<typeof deviceModelDtoSchema>;
export type DeviceCreateDto = z.infer<typeof deviceCreateDtoSchema>;
export type DeviceUpdateDto = z.infer<typeof deviceUpdateDtoSchema>;
export type DeviceDto = z.infer<typeof deviceDtoSchema>;
export type DeviceListItemDto = z.infer<typeof deviceListItemDtoSchema>;
export type DeviceDetailDto = z.infer<typeof deviceDetailDtoSchema>;
export type ArchivedDeviceListItemDto = z.infer<
  typeof archivedDeviceListItemDtoSchema
>;
export type ArchivedDeviceMatchDto = z.infer<
  typeof archivedDeviceMatchDtoSchema
>;
